import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import {
    deleteAccount,
    issueAccount,
    resetAccountPassword,
    updateAccountEnabled,
    type AccountIssued,
    type AccountPasswordReset,
} from '@/api/console';
import { OrgAdminApiError } from '@/api/orgAdmin';
import { Drawer, Modal, OptionCard, SkeletonTable, useToast } from '@/components/ui';
import { AccountsScreen } from './accounts/shared/AccountsScreen';
import { AccountsBulkBar, type AccountsBulkAction } from './accounts/shared/AccountsBulkBar';
import { AccountsPagination } from './accounts/shared/AccountsPagination';
import { AccountsTable } from './accounts/AccountsTable';
import { AccountsEmptyNew, AccountsEmptyFiltered } from './accounts/AccountsEmptyState';
import { BulkDeleteConfirmModal } from './accounts/BulkDeleteConfirmModal';
import { DeleteAccountDialog } from './accounts/DeleteAccountDialog';
import { LockAccountDialog } from './accounts/LockAccountDialog';
import { ResetAccountDrawer } from './accounts/ResetAccountDrawer';
import { ResetCredentialFields } from './accounts/ResetCredentialFields';
import { useResetCredential, type ResetCredentialMode } from './accounts/useResetCredential';
import { BulkResetResultPanel, type BulkResetResultRow } from './accounts/BulkResetResultPanel';
import { OneTimeSecretModal } from './accounts/OneTimeSecretModal';
import { AccountAuditPanel } from './audit/AccountAuditPanel';
import { useRealmAccountsAdapter } from './accounts/useRealmAccountsAdapter';
import { useAssignLevelToAccount } from './accounts/useAssignLevelToAccount';
import { AssignLevelToAccountDialog } from './accounts/AssignLevelToAccountDialog';
import { useDeactivateAccount } from './accounts/useDeactivateAccount';
import { DeactivateAccountDialog } from './accounts/DeactivateAccountDialog';
import { useBulkAssignAccountsToOrg } from './accounts/useBulkAssignAccountsToOrg';
import { BulkAssignAccountsToOrgDialog } from './accounts/BulkAssignAccountsToOrgDialog';
import { BulkAssignOrgResultPanel } from './accounts/BulkAssignOrgResultPanel';
import type { KeycloakAccountRow } from './accounts/types';

const EMPTY_FORM = {
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    credential_mode: 'temp_password' as 'invite' | 'temp_password',
};

const ACCOUNTS_TABLE_COLUMNS = 5;

/**
 * EAA-1820 (screen unification pass, ticket EAA-1820 reused — see the ticket
 * comment for why the same key covers both the original realm-screen build and
 * this later merge): `/console/accounts` now renders through the ONE shared
 * `AccountsScreen`, per the design pack's own governing rule quoted in the
 * ticket brief — realm and org account lists "dùng chung một UI: cùng tab, bộ
 * lọc đã lưu, rail bộ lọc, bảng và thanh thao tác. Khác nhau chỉ ở ngữ cảnh."
 *
 * REVERSAL FROM THE PREVIOUS DESIGN, recorded rather than silently dropped:
 * this screen and `ScopedAccountsPage.tsx` used to be two independent
 * implementations (EAA-1820 realm-only build, then EAA-1839 for the org
 * screen), each with its own facet rail, bulk bar, pagination and table
 * component, justified at the time by the two backends' very different
 * capabilities (a capped client-side Keycloak-realm walk vs. real server
 * pagination over `Assignment` rows). That justification for TWO components
 * stopped holding once `GET /api/v1/accounts` grew real server-side
 * pagination/search/filter/sort and a `/accounts/facets` aggregate endpoint
 * (this file used to drive `useAccountsScan`'s capped 2,000-row walk — DELETED,
 * not kept as a fallback, see `useRealmAccountsAdapter.ts`) — at that point the
 * only real difference between the two screens was the columns and the
 * write-flows, which is exactly what `shared/AccountsScreen` factors out.
 *
 * WHAT STILL DOES NOT EXIST, restated from the previous docstring so a later
 * session does not have to re-derive it:
 * 1. Account drill-in is `/console/accounts/:sub` (`AccountProfilePage`), fed by
 *    `GET /accounts/<sub>`. `AccountDetailDrawer` is retired from the list flow.
 * 2. UPDATED by EAA-1820 (frame 86) — this used to say there was no enable/
 *    disable toggle, only the irreversible `DELETE`. `PATCH /accounts/{sub}
 *    {enabled}` exists now (`AccountDetailView.patch`) and is wired to the row
 *    kebab's "Vô hiệu hoá tài khoản"/"Kích hoạt lại" (`useDeactivateAccount`).
 *    `DELETE` (hard, irreversible) is unchanged and stays a separate item.
 * 3. No true last-login timestamp; `lastLoginAvailable` gates an explicit
 *    "Chưa khả dụng" vs. "Chưa ghi nhận" vs. a real date (see
 *    `AccountsTable.tsx`).
 */
export function AccountsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const can = useCan();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();
    const toast = useToast();

    const canIssue = can('console.accounts.issue');
    const canDelete = can('console.accounts.delete');
    const canReset = can('console.accounts.reset');
    // EAA-1820 (frame 86): `console.accounts.lock` gates PATCH /accounts/{sub}
    // (AccountDetailView.patch — see updateAccountEnabled's docstring).
    const canLock = can('console.accounts.lock');
    const canViewAudit = can('console.audit.view');

    const [tab, setTab] = useState<'accounts' | 'audit'>('accounts');

    const listEnabled = authenticated && Boolean(accessToken) && (canIssue || canReset || canDelete);
    const adapter = useRealmAccountsAdapter(config.apiBase, accessToken, listEnabled);

    function handleViewDetail(row: KeycloakAccountRow) {
        navigate(`/console/accounts/${encodeURIComponent(row.sub)}`);
    }

    // EAA-1820 (frames 84/86, row kebab): "Gán Level" and "Vô hiệu hoá tài
    // khoản"/"Kích hoạt lại" — see each hook's docstring for why these are
    // realm-specific rather than shared with the org-scoped screen.
    const assignLevel = useAssignLevelToAccount(adapter.invalidate);
    const deactivate = useDeactivateAccount(adapter.invalidate);
    // EAA-1820 (frame 75, bulk bar): "Gán vào đơn vị" — grants the selected
    // identities into one đơn vị + Level at once. See the hook's docstring for
    // why this is N sequential creates and not one bulk request.
    const bulkAssignOrg = useBulkAssignAccountsToOrg(adapter.invalidate, () => adapter.setSelected(new Set()));

    // ---- issue ----
    const [form, setForm] = useState(EMPTY_FORM);
    const [formError, setFormError] = useState<string | null>(null);
    const [issued, setIssued] = useState<AccountIssued | null>(null);
    const [issueModalOpen, setIssueModalOpen] = useState(false);

    const issueMutation = useMutation({
        mutationFn: () =>
            issueAccount(config.apiBase, accessToken as string, {
                username: form.username.trim(),
                ...(form.email.trim() ? { email: form.email.trim() } : {}),
                ...(form.first_name.trim() ? { first_name: form.first_name.trim() } : {}),
                ...(form.last_name.trim() ? { last_name: form.last_name.trim() } : {}),
                credential_mode: form.credential_mode,
            }),
        onSuccess: (data) => {
            setIssueModalOpen(false);
            setIssued(data);
            setForm(EMPTY_FORM);
            setFormError(null);
            toast.success(t('console.accounts.toasts.issueSuccess', { username: data.username }));
            adapter.invalidate();
        },
        onError: (error) => {
            const message =
                error instanceof OrgAdminApiError ? error.message : t('console.accounts.toasts.issueErrorFallback');
            setFormError(message);
            toast.error(t('console.accounts.toasts.issueError', { username: form.username.trim() }));
        },
    });

    // ---- single delete ----
    const [deleteTarget, setDeleteTarget] = useState<KeycloakAccountRow | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const deleteMutation = useMutation({
        mutationFn: (sub: string) => deleteAccount(config.apiBase, accessToken as string, sub),
        onSuccess: (_data, sub) => {
            setDeleteTarget(null);
            setDeleteError(null);
            adapter.setSelected((prev) => {
                const next = new Set(prev);
                next.delete(sub);
                return next;
            });
            toast.success(t('console.accounts.toasts.deleteSuccess', { sub }));
            adapter.invalidate();
        },
        onError: (error) => {
            const message =
                error instanceof OrgAdminApiError ? error.message : t('console.accounts.toasts.deleteErrorFallback');
            setDeleteError(message);
            toast.error(t('console.accounts.toasts.deleteError', { sub: deleteTarget?.sub ?? '' }));
        },
    });

    function openDeleteFor(row: KeycloakAccountRow) {
        setDeleteTarget(row);
        setDeleteError(null);
    }

    // ---- profile lock (frame 80) ----
    const [lockTarget, setLockTarget] = useState<KeycloakAccountRow | null>(null);
    const [lockError, setLockError] = useState<string | null>(null);

    const lockMutation = useMutation({
        mutationFn: ({ sub, endSessions }: { sub: string; username: string; endSessions: boolean }) =>
            updateAccountEnabled(config.apiBase, accessToken as string, sub, false, { endSessions }),
        onSuccess: (_data, { username }) => {
            setLockTarget(null);
            setLockError(null);
            toast.success(t('console.accounts.lockModal.success', { username }));
            adapter.invalidate();
        },
        onError: (error) => {
            setLockError(error instanceof OrgAdminApiError ? error.message : t('console.accounts.deactivate.errorFallback'));
        },
    });

    const [resetIdent, setResetIdent] = useState('');
    const [resetTarget, setResetTarget] = useState<KeycloakAccountRow | null>(null);
    const [resetResult, setResetResult] = useState<AccountPasswordReset | null>(null);
    const [resetError, setResetError] = useState<string | null>(null);
    const [resetModalOpen, setResetModalOpen] = useState(false);

    // EAA-1779: the credential choice (generated temp vs. an admin-typed
    // password) for the single-account drawer. Shares its rules with the bulk
    // dialog's own instance below via `useResetCredential`.
    const resetCredential = useResetCredential();

    const resetMutation = useMutation({
        mutationFn: ({ ident, endSessions }: { ident: string; endSessions: boolean }) => {
            const trimmed = ident.trim();
            const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed);
            const identity = isUuid ? { sub: trimmed } : { username: trimmed };
            return resetAccountPassword(config.apiBase, accessToken as string, {
                ...identity,
                end_sessions: endSessions,
                ...resetCredential.toRequest(),
            });
        },
        onSuccess: (data) => {
            setResetModalOpen(false);
            // EAA-1779: only the generated mode produces a secret to reveal. In
            // admin-set mode the operator already holds the value, so opening the
            // one-time panel would show an empty box.
            setResetResult(data.mode === 'admin_set' ? null : data);
            setResetIdent('');
            setResetError(null);
            resetCredential.clear();
            toast.success(t('console.accounts.toasts.resetSuccess', { username: data.username }));
            adapter.invalidate();
        },
        onError: (error) => {
            const message =
                error instanceof OrgAdminApiError ? error.message : t('console.accounts.toasts.resetErrorFallback');
            setResetError(message);
            toast.error(t('console.accounts.toasts.resetError', { ident: resetIdent.trim() }));
        },
    });

    function openResetFor(row: KeycloakAccountRow) {
        setResetTarget(row);
        setResetIdent(row.sub);
        setResetError(null);
        setResetModalOpen(true);
    }

    // ---- bulk delete ----
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
    const [bulkDeletePending, setBulkDeletePending] = useState(false);

    function openBulkDelete() {
        if (adapter.selected.size === 1) {
            const sub = [...adapter.selected][0];
            const row = adapter.rows.find((r) => r.sub === sub);
            if (row) {
                openDeleteFor(row);
            }
            return;
        }
        setBulkDeleteModalOpen(true);
    }

    async function runBulkDelete() {
        setBulkDeletePending(true);
        const subs = [...adapter.selected];
        const results = await Promise.allSettled(subs.map((sub) => deleteAccount(config.apiBase, accessToken as string, sub)));
        const failed = subs.filter((_, i) => results[i].status === 'rejected');
        setBulkDeletePending(false);
        setBulkDeleteModalOpen(false);
        adapter.setSelected(new Set(failed));
        if (failed.length === 0) {
            toast.success(t('console.accounts.toasts.bulkDeleteSuccess', { count: subs.length }));
        } else {
            toast.error(
                t('console.accounts.toasts.bulkDeletePartial', {
                    succeeded: subs.length - failed.length,
                    total: subs.length,
                    failed: failed.length,
                })
            );
        }
        adapter.invalidate();
    }

    // ---- bulk reset ----
    const [bulkResetConfirmOpen, setBulkResetConfirmOpen] = useState(false);
    const [bulkResetPending, setBulkResetPending] = useState(false);
    const [bulkResetResults, setBulkResetResults] = useState<BulkResetResultRow[] | null>(null);
    const [bulkResetMode, setBulkResetMode] = useState<ResetCredentialMode>('temp_password');
    // EAA-1779: its own credential state, separate from the drawer's — the two
    // dialogs can be opened one after the other and a password typed for a
    // single account must not leak into a bulk run over six.
    const bulkCredential = useResetCredential();

    async function runBulkReset() {
        setBulkResetPending(true);
        const targets = adapter.rows.filter((r) => adapter.selected.has(r.sub));
        // EAA-1779: ONE credential body, reused for every selected account — in
        // admin-set mode that is what "one shared password" means. There is no
        // bulk endpoint; this is still a per-account loop, so each row keeps its
        // own audit entry and its own success/failure.
        const credentialBody = bulkCredential.toRequest();
        const settled = await Promise.allSettled(
            targets.map((r) =>
                resetAccountPassword(config.apiBase, accessToken as string, {
                    sub: r.sub,
                    ...credentialBody,
                })
            )
        );
        const results: BulkResetResultRow[] = targets.map((r, i) => {
            const outcome = settled[i];
            if (outcome.status === 'fulfilled') {
                return {
                    sub: r.sub,
                    username: outcome.value.username,
                    temp_password: outcome.value.temp_password ?? undefined,
                    ok: true,
                };
            }
            const message =
                outcome.reason instanceof OrgAdminApiError ? outcome.reason.message : t('console.accounts.toasts.unknownError');
            return { sub: r.sub, username: r.username, ok: false, error: message };
        });
        setBulkResetPending(false);
        setBulkResetConfirmOpen(false);
        // Captured BEFORE clearing, so the result panel renders the mode the run
        // actually used rather than the freshly-reset default.
        setBulkResetMode(bulkCredential.mode);
        bulkCredential.clear();
        setBulkResetResults(results);
        adapter.setSelected(new Set(results.filter((r) => !r.ok).map((r) => r.sub)));
        adapter.invalidate();
    }

    if (!listEnabled) {
        return null;
    }

    const hasEmail = Boolean(form.email.trim());

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (!form.username.trim()) {
            setFormError(t('console.accounts.validation.usernameRequired'));
            return;
        }
        if (form.credential_mode === 'invite' && !hasEmail) {
            setFormError(t('console.accounts.validation.inviteNeedsEmail'));
            return;
        }
        issueMutation.mutate();
    };

    const closeIssueModal = () => {
        setIssueModalOpen(false);
        setFormError(null);
    };
    const closeResetDrawer = () => {
        setResetModalOpen(false);
        setResetTarget(null);
        setResetError(null);
    };

    const showEmptyNew = adapter.listQuery.isSuccess && adapter.rows.length === 0 && !adapter.hasActiveFilters;
    const showEmptyFiltered = adapter.listQuery.isSuccess && adapter.rows.length === 0 && adapter.hasActiveFilters;

    const bulkActions: AccountsBulkAction[] = [
        // EAA-1820 (frame 75): "Gán vào đơn vị" — opens the bulk org/Level
        // dialog over exactly the rows currently ticked. Gated the same coarse
        // way as the row-level "Gán Level" kebab item (`canIssue`) since it is
        // the same underlying `createOrgAssignment` write, re-checked per org
        // server-side.
        ...(canIssue
            ? [
                  {
                      key: 'assign-org',
                      label: t('console.accounts.bulkBar.assignOrgAction'),
                      onClick: () => bulkAssignOrg.open(adapter.rows.filter((r) => adapter.selected.has(r.sub))),
                  },
              ]
            : []),
        ...(canReset
            ? [{ key: 'reset', label: t('console.accounts.bulkBar.resetAction'), onClick: () => setBulkResetConfirmOpen(true) }]
            : []),
        ...(canDelete
            ? [{ key: 'delete', label: t('console.accounts.bulkBar.deleteAction'), danger: true, onClick: openBulkDelete }]
            : []),
    ];

    let body: React.ReactNode;
    if (adapter.listQuery.isLoading) {
        body = (
            <div role="status" aria-live="polite">
                <span className="portal-sr-only">{t('common.loading')}</span>
                <SkeletonTable rows={6} columns={ACCOUNTS_TABLE_COLUMNS} />
            </div>
        );
    } else if (adapter.listQuery.isError) {
        body = (
            <p className="portal-admin-error">
                {t('console.accounts.page.loadError')}{' '}
                <button type="button" className="portal-btn" onClick={() => adapter.listQuery.refetch()}>
                    {t('console.accounts.page.retryButton')}
                </button>
            </p>
        );
    } else if (showEmptyNew) {
        body = <AccountsEmptyNew canIssue={canIssue} onIssue={() => setIssueModalOpen(true)} />;
    } else if (showEmptyFiltered) {
        body = <AccountsEmptyFiltered search={adapter.search} onClear={adapter.clearFilters} />;
    } else {
        body = (
            <>
                <AccountsBulkBar
                    selectedCount={adapter.selected.size}
                    actions={bulkActions}
                    onClear={() => adapter.setSelected(new Set())}
                    busy={bulkDeletePending || bulkResetPending}
                />
                <AccountsTable
                    rows={adapter.rows}
                    selected={adapter.selected}
                    onSelectedChange={adapter.setSelected}
                    sortField={adapter.sortField}
                    sortDirection={adapter.sortDirection}
                    onSort={adapter.onSort}
                    onViewDetail={handleViewDetail}
                    onResetOne={openResetFor}
                    onDeleteOne={openDeleteFor}
                    onAssignLevel={assignLevel.open}
                    onDeactivate={deactivate.open}
                    canReset={canReset}
                    canDelete={canDelete}
                    canAssignLevel={canIssue}
                    canLock={canLock}
                    lastLoginAvailable={adapter.lastLoginAvailable}
                />
                <AccountsPagination
                    page={adapter.page}
                    totalPages={adapter.totalPages}
                    pageSize={adapter.pageSize}
                    onPageSizeChange={adapter.setPageSize}
                    rangeFrom={adapter.rangeFrom}
                    rangeTo={adapter.rangeTo}
                    total={adapter.total}
                    onGoTo={adapter.setPage}
                    pageSizeOptions={adapter.pageSizeOptions}
                />
            </>
        );
    }

    return (
        <>
            <AccountsScreen
                context="realm"
                title={t('console.accounts.page.title')}
                subtitle={''}
                headerActions={
                    canIssue && (
                        <button type="button" className="portal-btn portal-btn-primary" onClick={() => setIssueModalOpen(true)}>
                            {t('console.accounts.page.createButton')}
                        </button>
                    )
                }
                tab={tab}
                onTabChange={setTab}
                showAuditTab={canViewAudit}
                auditPanel={<AccountAuditPanel />}
                facetGroups={adapter.facetGroups}
                onSelectFacet={adapter.selectFacet}
                hasActiveFilters={adapter.hasActiveFilters}
                onClearFilters={adapter.clearFilters}
                search={adapter.search}
                onSearchChange={adapter.setSearch}
                activeFilterCount={adapter.activeFilterCount}
                rangeLabel={t('console.accounts.filters.shownCount', {
                    shown: adapter.rows.length,
                    total: adapter.total,
                })}
                body={body}
            />

            <OneTimeSecretModal
                secret={
                    issued
                        ? { kind: 'created', username: issued.username, sub: issued.sub, tempPassword: issued.temp_password }
                        : resetResult
                          ? {
                                kind: 'reset',
                                username: resetResult.username,
                                tempPassword: resetResult.temp_password ?? undefined,
                            }
                          : null
                }
                onClose={() => {
                    setIssued(null);
                    setResetResult(null);
                }}
            />

            {bulkResetResults && (
                <div className="acc-detail-line">
                    <BulkResetResultPanel
                        results={bulkResetResults}
                        mode={bulkResetMode}
                        onDismiss={() => setBulkResetResults(null)}
                    />
                </div>
            )}

            <AssignLevelToAccountDialog vm={assignLevel} />
            <DeactivateAccountDialog vm={deactivate} />
            <BulkAssignAccountsToOrgDialog vm={bulkAssignOrg} />
            {bulkAssignOrg.results && (
                <div className="acc-detail-line">
                    <BulkAssignOrgResultPanel results={bulkAssignOrg.results} onDismiss={bulkAssignOrg.dismissResults} />
                </div>
            )}

            {canLock && (
                <LockAccountDialog
                    row={lockTarget}
                    pending={lockMutation.isPending}
                    error={lockError}
                    onClose={() => setLockTarget(null)}
                    onConfirm={(endSessions) =>
                        lockTarget &&
                        lockMutation.mutate({ sub: lockTarget.sub, username: lockTarget.username, endSessions })
                    }
                />
            )}

            {canDelete && (
                <DeleteAccountDialog
                    row={deleteTarget}
                    pending={deleteMutation.isPending}
                    error={deleteError}
                    onClose={() => setDeleteTarget(null)}
                    onConfirm={(sub) => deleteMutation.mutate(sub)}
                />
            )}

            {canDelete && (
                <BulkDeleteConfirmModal
                    open={bulkDeleteModalOpen}
                    count={adapter.selected.size}
                    pending={bulkDeletePending}
                    onCancel={() => setBulkDeleteModalOpen(false)}
                    onConfirm={runBulkDelete}
                />
            )}

            {canReset && (
                <Modal
                    open={bulkResetConfirmOpen}
                    onCancel={() => setBulkResetConfirmOpen(false)}
                    title={t('console.accounts.bulkResetModal.title')}
                    footer={
                        <>
                            <button
                                type="button"
                                className="portal-btn"
                                onClick={() => setBulkResetConfirmOpen(false)}
                                disabled={bulkResetPending}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="button"
                                className="portal-btn portal-btn-primary"
                                onClick={runBulkReset}
                                disabled={bulkResetPending || !bulkCredential.valid}
                            >
                                {bulkResetPending
                                    ? t('console.accounts.bulkResetModal.pending')
                                    : t('console.accounts.bulkResetModal.submit', { count: adapter.selected.size })}
                            </button>
                        </>
                    }
                >
                    <p className="portal-admin-hint">
                        {bulkCredential.mode === 'admin_set'
                            ? t('console.accounts.bulkResetModal.hintAdminSet', { count: adapter.selected.size })
                            : t('console.accounts.bulkResetModal.hint', { count: adapter.selected.size })}
                    </p>
                    <ResetCredentialFields credential={bulkCredential} disabled={bulkResetPending} />
                    {/* EAA-1779: the "save it now, it is shown once" warning only
                        applies to the generated mode — an admin-typed password is
                        never revealed back, so there is nothing to write down. */}
                    {bulkCredential.mode !== 'admin_set' && (
                        <p className="portal-accounts-onetime-warning">
                            {t('console.accounts.bulkResetModal.onetimeWarning')}
                        </p>
                    )}
                </Modal>
            )}

            {canIssue && (
                <Drawer
                    open={issueModalOpen}
                    onClose={closeIssueModal}
                    title={t('console.accounts.issueDrawer.title')}
                    width={520}
                    footer={
                        <>
                            <button type="button" className="portal-btn" onClick={closeIssueModal} disabled={issueMutation.isPending}>
                                {t('common.cancel')}
                            </button>
                            <button
                                type="submit"
                                form="accounts-issue-form"
                                className="portal-btn portal-btn-primary"
                                disabled={issueMutation.isPending}
                            >
                                {issueMutation.isPending
                                    ? t('console.accounts.issueDrawer.pending')
                                    : t('console.accounts.issueDrawer.title')}
                            </button>
                        </>
                    }
                >
                    <form id="accounts-issue-form" className="portal-admin-form" onSubmit={onSubmit}>
                        <p className="portal-admin-hint">{t('console.accounts.issueDrawer.introHint')}</p>
                        <label>
                            {t('console.accounts.issueDrawer.usernameLabel')}
                            <input
                                value={form.username}
                                onChange={(e) => setForm({ ...form, username: e.target.value })}
                                placeholder={t('console.accounts.issueDrawer.usernamePlaceholder')}
                                required
                            />
                        </label>
                        <label>
                            {t('console.accounts.issueDrawer.emailLabel')}
                            <input
                                type="email"
                                value={form.email}
                                onChange={(e) => {
                                    const email = e.target.value;
                                    setForm((prev) => ({
                                        ...prev,
                                        email,
                                        credential_mode: email.trim() ? prev.credential_mode : 'temp_password',
                                    }));
                                }}
                                placeholder={t('console.accounts.issueDrawer.emailPlaceholder')}
                            />
                        </label>
                        <label>
                            {t('console.accounts.issueDrawer.lastNameLabel')}
                            <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                        </label>
                        <label>
                            {t('console.accounts.issueDrawer.firstNameLabel')}
                            <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                        </label>
                        <fieldset className="portal-console-radio-group acc-cred-modes">
                            <legend>{t('console.accounts.issueDrawer.credModeLegend')}</legend>
                            <OptionCard
                                inputType="radio"
                                name="credential_mode"
                                selected={form.credential_mode === 'invite'}
                                disabled={!hasEmail}
                                onSelect={() => setForm({ ...form, credential_mode: 'invite' })}
                                title={t('console.accounts.issueDrawer.inviteModeLabel')}
                                description={
                                    <>
                                        {t('console.accounts.issueDrawer.inviteModeDesc')}
                                        {!hasEmail &&
                                            ` ${t('console.accounts.issueDrawer.inviteModeNeedsEmailSuffix')}`}
                                    </>
                                }
                            />
                            <OptionCard
                                inputType="radio"
                                name="credential_mode"
                                selected={form.credential_mode === 'temp_password'}
                                onSelect={() => setForm({ ...form, credential_mode: 'temp_password' })}
                                title={t('console.accounts.issueDrawer.tempPasswordModeLabel')}
                                description={t('console.accounts.issueDrawer.tempPasswordModeDesc')}
                            />
                        </fieldset>
                        <p className="portal-admin-hint">{t('console.accounts.issueDrawer.assignHint')}</p>
                        <p className="portal-admin-hint">{t('console.accounts.issueDrawer.auditHint')}</p>
                        {formError && <p className="portal-admin-error">{formError}</p>}
                    </form>
                </Drawer>
            )}

            {canReset && (
                <ResetAccountDrawer
                    open={resetModalOpen}
                    target={resetTarget}
                    ident={resetIdent}
                    credential={resetCredential}
                    pending={resetMutation.isPending}
                    error={resetError}
                    onIdentChange={setResetIdent}
                    onClose={closeResetDrawer}
                    onSubmit={({ endSessions }) => {
                        if (!resetIdent.trim()) {
                            setResetError(t('console.accounts.validation.resetIdentRequired'));
                            return;
                        }
                        resetMutation.mutate({ ident: resetIdent, endSessions });
                    }}
                />
            )}
        </>
    );
}

export default AccountsPage;
